import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Plus, X } from 'lucide-react';

import { cardChargebackApi } from '@/api/cardChargeback.api';
import { cardAcquirerApi }   from '@/api/cardAcquirer.api';
import { Spinner }           from '@/components/ui/Spinner';
import { Button }            from '@/components/ui/Button';
import { Input }             from '@/components/ui/Input';
import { Select }            from '@/components/ui/Select';
import { ListPager }         from '@/components/lists/ListPager';
import { ListSearch, DateRangeFilter } from '@/components/lists/ListFilters';
import { usePermission }     from '@/hooks/usePermission';
import { Permissions }       from '@/constants/permissions';
import { cn }                from '@/utils/cn';

import type {
  CardChargebackDto, CardChargebackFilterDto, CardAcquirerListDto,
} from '@/types/cardReconciliation.types';

/**
 * Chargebacks — a customer disputing a card payment, and what the business does about it.
 *
 * The endpoints have always been here: raise one, and then act on it. There was no screen, so a
 * chargeback could only ever arrive as a deduction on a settlement file with nothing recording
 * what happened next.
 *
 * The four actions are the acquirer's vocabulary, not ours: accept the loss, dispute it, recover
 * the money, or write it off. Which are offered depends on where the chargeback has got to —
 * a pending one can be accepted or disputed; a disputed one is then recovered or written off.
 */

const STATUS_STYLE: Record<string, string> = {
  Pending:    'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  Accepted:   'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300',
  Disputed:   'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  Recovered:  'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  WrittenOff: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
};

/** What may be done next, by where the chargeback has got to. */
const ACTIONS_FOR: Record<string, { action: string; label: string }[]> = {
  Pending:  [{ action: 'Accept',   label: 'Accept the loss' },
             { action: 'Dispute',  label: 'Dispute it' }],
  Disputed: [{ action: 'Recover',  label: 'Recovered' },
             { action: 'WriteOff', label: 'Write off' }],
  Accepted: [{ action: 'Recover',  label: 'Recovered' },
             { action: 'WriteOff', label: 'Write off' }],
};

export const CardChargebackListPage = () => {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(Permissions.CARD_CHARGEBACK_CREATE);
  const canPost   = hasPermission(Permissions.CARD_CHARGEBACK_POST);

  const [items,   setItems]   = useState<CardChargebackDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 50;

  const [search,   setSearch]   = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  const [acquirers, setAcquirers] = useState<CardAcquirerListDto[]>([]);
  const [error,   setError]   = useState<string | null>(null);
  const [notice,  setNotice]  = useState<string | null>(null);

  const [showNew, setShowNew] = useState(false);
  const [acting,  setActing]  = useState<{ cb: CardChargebackDto; action: string } | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [busy, setBusy] = useState(false);

  // New chargeback
  const [form, setForm] = useState({
    chargebackNo: '', cardAcquirerId: '', originalSaleReference: '',
    chargebackDate: new Date().toISOString().slice(0, 10),
    chargebackReason: '', amount: '', notes: '',
  });

  const load = useCallback(async (filter: CardChargebackFilterDto) => {
    setLoading(true);
    try {
      const res  = await cardChargebackApi.getList(filter);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } catch {
      setError('Could not load chargebacks.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cardAcquirerApi.getList(true)
      .then(res => setAcquirers(res.data?.data ?? []))
      .catch(() => { /* the list still works without the names */ });
  }, []);

  useEffect(() => {
    load({ search: search || undefined, dateFrom: dateFrom || undefined,
           dateTo: dateTo || undefined, page, pageSize });
  }, [page]); // eslint-disable-line

  const refresh = () => load({ search: search || undefined, dateFrom: dateFrom || undefined,
                               dateTo: dateTo || undefined, page, pageSize });

  const handleSearch = () => { setPage(1);
    load({ search: search || undefined, dateFrom: dateFrom || undefined,
           dateTo: dateTo || undefined, page: 1, pageSize }); };

  const serverMessage = (err: unknown, fallback: string) => {
    const body = (err as { response?: { data?: { message?: string;
                   errors?: { message?: string }[] } } }).response?.data;
    return body?.errors?.[0]?.message ?? body?.message ?? fallback;
  };

  const create = async () => {
    setError(null);
    if (!form.chargebackNo.trim())   { setError('Give the chargeback a reference.'); return; }
    if (!form.cardAcquirerId)        { setError('Choose which acquirer raised it.'); return; }
    const amount = Number(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) { setError('Enter the amount being charged back.'); return; }

    setBusy(true);
    try {
      await cardChargebackApi.create({
        chargebackNo:          form.chargebackNo.trim(),
        cardAcquirerId:        form.cardAcquirerId,
        originalSaleReference: form.originalSaleReference.trim() || null,
        chargebackDate:        form.chargebackDate,
        chargebackReason:      form.chargebackReason.trim() || null,
        amount,
        notes:                 form.notes.trim() || null,
      });
      setNotice(`Chargeback ${form.chargebackNo.trim()} recorded.`);
      setShowNew(false);
      setForm({ chargebackNo: '', cardAcquirerId: '', originalSaleReference: '',
                chargebackDate: new Date().toISOString().slice(0, 10),
                chargebackReason: '', amount: '', notes: '' });
      refresh();
    } catch (err) {
      setError(serverMessage(err, 'The chargeback was refused.'));
    } finally { setBusy(false); }
  };

  const runAction = async () => {
    if (!acting) return;
    // The server requires a reason on a dispute; saying so here saves the round trip.
    if (acting.action === 'Dispute' && !actionNotes.trim()) {
      setError('A dispute needs a reason.'); return;
    }
    setBusy(true); setError(null);
    try {
      await cardChargebackApi.action(acting.cb.id, acting.action, actionNotes.trim() || null);
      setNotice(`${acting.cb.chargebackNo} — ${acting.action.toLowerCase()} recorded.`);
      setActing(null); setActionNotes('');
      refresh();
    } catch (err) {
      setError(serverMessage(err, 'That action was refused.'));
    } finally { setBusy(false); }
  };

  const money = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Card Chargebacks</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total.toLocaleString()} recorded
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canCreate && (
              <Button size="sm" onClick={() => { setShowNew(v => !v); setError(null); }}
                      leftIcon={<Plus className="w-3.5 h-3.5" />}>
                Record a chargeback
              </Button>
            )}
            <button onClick={refresh} title="Refresh"
                    className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400">
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        {showNew && (
          <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input label="Reference" value={form.chargebackNo} maxLength={50}
                     onChange={e => setForm(f => ({ ...f, chargebackNo: e.target.value }))}
                     placeholder="e.g. CB-2026-0001" />
              <Select label="Acquirer" value={form.cardAcquirerId} placeholder="— choose —"
                      onChange={e => setForm(f => ({ ...f, cardAcquirerId: e.target.value }))}
                      options={acquirers.map(a => ({ value: a.id, label: `${a.acquirerCode} — ${a.acquirerName}` }))} />
              <Input label="Amount" value={form.amount} inputMode="decimal"
                     onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
              <Input label="Chargeback date" type="date" value={form.chargebackDate}
                     onChange={e => setForm(f => ({ ...f, chargebackDate: e.target.value }))} />
              <Input label="Original sale reference" value={form.originalSaleReference} maxLength={50}
                     onChange={e => setForm(f => ({ ...f, originalSaleReference: e.target.value }))}
                     placeholder="The transaction being disputed" />
              <Input label="Reason" value={form.chargebackReason} maxLength={200}
                     onChange={e => setForm(f => ({ ...f, chargebackReason: e.target.value }))}
                     placeholder="What the cardholder claims" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowNew(false)}>Cancel</Button>
              <Button onClick={create} loading={busy} disabled={busy}>Record it</Button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <ListSearch value={search} onChange={setSearch} onSubmit={handleSearch}
                      placeholder="Search reference, sale reference…" />
          <div className="flex items-center gap-2 ml-auto">
            <DateRangeFilter label="Chargeback date"
              from={dateFrom || undefined} to={dateTo || undefined}
              onChange={({ from, to }) => { setDateFrom(from ?? ''); setDateTo(to ?? ''); }} />
            <button onClick={handleSearch}
                    className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg">
              Search
            </button>
          </div>
        </div>
      </div>

      {/* While the dialog is open it shows its own error; a second copy behind it reads as two
          separate problems. */}
      {((error && !acting) || notice) && (
        <div className="px-6 pt-4">
          <div className={cn('rounded-md px-4 py-3 text-sm flex items-start justify-between gap-3',
            error
              ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
              : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300')}>
            <span>{error ?? notice}</span>
            <button onClick={() => { setError(null); setNotice(null); }} aria-label="Dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-auto px-6 py-4">
        {loading ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-sm text-slate-500 dark:text-slate-400">
            No chargebacks recorded for this filter.
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900/40">
                <tr>
                  {['Reference', 'Acquirer', 'Date', 'Original sale', 'Reason', 'Amount', 'Status', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {items.map(cb => {
                  const next = canPost ? (ACTIONS_FOR[cb.statusName] ?? []) : [];
                  return (
                    <tr key={cb.id} className="text-sm hover:bg-slate-50 dark:hover:bg-slate-700/30">
                      <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{cb.chargebackNo}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{cb.acquirerName}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{fmtDate(cb.chargebackDate)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                        {cb.originalSaleReference ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{cb.chargebackReason ?? '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-900 dark:text-white">
                        {money(cb.amount)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('px-2 py-0.5 rounded text-xs font-medium',
                          STATUS_STYLE[cb.statusName] ?? 'bg-slate-100 text-slate-700')}>
                          {cb.statusName}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 justify-end">
                          {next.map(a => (
                            <button key={a.action}
                              onClick={() => { setActing({ cb, action: a.action }); setActionNotes(''); setError(null); }}
                              className="px-2 py-1 text-xs rounded border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">
                              {a.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-4">
            <ListPager page={page} totalPages={totalPages} totalCount={total}
                       onChange={setPage} noun="chargebacks" />
          </div>
        )}
      </div>

      {acting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-md p-5 space-y-4">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">
              {acting.action === 'Accept'   && 'Accept the loss'}
              {acting.action === 'Dispute'  && 'Dispute this chargeback'}
              {acting.action === 'Recover'  && 'Mark as recovered'}
              {acting.action === 'WriteOff' && 'Write this off'}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {acting.cb.chargebackNo} — {money(acting.cb.amount)} from {acting.cb.acquirerName}.
              {acting.action === 'Dispute' && ' A dispute needs a reason.'}
              {(acting.action === 'Recover' || acting.action === 'WriteOff') && ' This posts a journal.'}
            </p>
            <Input label={acting.action === 'Dispute' ? 'Reason' : 'Notes'}
                   value={actionNotes} maxLength={500}
                   onChange={e => setActionNotes(e.target.value)}
                   placeholder={acting.action === 'Dispute' ? 'Why this is being challenged' : 'Optional'} />
            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => { setActing(null); setError(null); }}>Cancel</Button>
              <Button onClick={runAction} loading={busy} disabled={busy}>Confirm</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
